// src/utils/authUtils.js

/**
 * Утилиты для работы с аутентификацией
 */
import Cookies from 'js-cookie'
import config from '../config'; 

const isDevelopment = process.env.NODE_ENV === 'development';

// Common cookie options
const cookieOptions = {
  path: '/',                 // Cookie is valid for the entire domain
  domain: isDevelopment ? 'localhost' : `.${config.VITE_BASE_DOMAIN}`, // Valid for all subdomains (if needed)
  secure: true,              // Only sent over HTTPS
  sameSite: 'None',          // Allow cross-origin requests (for different subdomains)
  httpOnly: false,            // Ensure it's not accessible via JavaScript (good for security)
  expires: 7                 // Cookie expiration (optional, in days)
};

/**
 * Helper function to set cookies with common options
 * @param {string} key - The cookie key (e.g., 'jwt_token')
 * @param {string} value - The cookie value
 */
const setCookie = (key, value) => {
  Cookies.set(key, value, cookieOptions);
};

// Ключи для Cookies
export const STORAGE_KEYS = {
    TOKEN: 'access_token',
    REFRESH_TOKEN: 'refresh_token',
    USER: 'user',
    USER_LANGUAGE: 'userLanguage',
    THEME: 'siteTheme'
  };
  
  
  
  
  /**
   * Получить данные пользователя из Cookies
   * @returns {Object|null} Данные пользователя
   */
  export const getUser = () => {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  };
  
  /**
   * Сохранить данные пользователя в Cookies
   * @param {Object} user - Данные пользователя
   */
  export const setUser = (user) => {
    try {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    } catch (error) {
      console.error('Error storing user data:', error);
    }
  };
  
  /**
   * Очистить данные пользователя
   */
  export const clearUser = () => {
    localStorage.removeItem(STORAGE_KEYS.USER);
  };
  
  /**
   * Получить язык пользователя
   * @returns {string} Код языка
   */
  export const getUserLanguage = () => {
    return localStorage.getItem(STORAGE_KEYS.USER_LANGUAGE) || 
           navigator.language.split('-')[0] || 
           'en';
  };
  
  /**
   * Установить язык пользователя
   * @param {string} language - Код языка
   */
  export const setUserLanguage = (language) => {
    localStorage.setItem(STORAGE_KEYS.USER_LANGUAGE, language);
  };
    
  /**
   * Очистить все данные аутентификации
   */
  export const clearAuthData = () => {
    clearUser();
  };
  
  
  /**
   * Форматировать ошибки API для отображения пользователю
   * @param {Error} error - Ошибка от API
   * @returns {string} Отформатированное сообщение об ошибке
   */
  export const formatAuthError = (error) => {
    if (error.response?.data) {
      const { data } = error.response;
      
      // Обрабатываем различные форматы ошибок от API
      if (typeof data === 'string') {
        return data;
      }
      
      if (data.detail) {
        return data.detail;
      }
      
      if (data.message) {
        return data.message;
      }
      
      if (data.error) {
        return data.error;
      }
      
      // Обрабатываем ошибки валидации
      if (data.errors) {
        const errorMessages = Object.values(data.errors).flat();
        return errorMessages.join(', ');
      }
      
      // Обрабатываем ошибки полей
      const fieldErrors = [];
      Object.keys(data).forEach(field => {
        if (Array.isArray(data[field])) {
          fieldErrors.push(...data[field]);
        }
      });
      
      if (fieldErrors.length > 0) {
        return fieldErrors.join(', ');
      }
    }
    
    // Сообщения по умолчанию
    if (error.response?.status === 401) {
      return 'Неверные учетные данные';
    }
    
    if (error.response?.status === 403) {
      return 'Доступ запрещен';
    }
    
    if (error.response?.status === 404) {
      return 'Ресурс не найден';
    }
    
    if (error.response?.status >= 500) {
      return 'Ошибка сервера. Попробуйте позже';
    }
    
    return error.message || 'Произошла неизвестная ошибка';
  };
  
  /**
   * Создать заголовки авторизации для API запросов
   * @returns {Object} Заголовки с токеном авторизации
   */
  export const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  /**
   * Хелпер для проверки ролей пользователя (если используется)
   * @param {Object} user - Данные пользователя
   * @param {string|Array} roles - Роль или массив ролей для проверки
   * @returns {boolean} true если у пользователя есть нужная роль
   */
  export const hasRole = (user, roles) => {
    if (!user || !user.roles) return false;
    
    const userRoles = Array.isArray(user.roles) ? user.roles : [user.roles];
    const checkRoles = Array.isArray(roles) ? roles : [roles];
    
    return checkRoles.some(role => userRoles.includes(role));
  };
  
  /**
   * Проверить, является ли пользователь администратором
   * @param {Object} user - Данные пользователя
   * @returns {boolean} true если пользователь администратор
   */
  export const isAdmin = (user) => {
    return hasRole(user, ['admin', 'administrator']);
  };
  
  /**
   * Получить полное имя пользователя
   * @param {Object} user - Данные пользователя
   * @returns {string} Полное имя или email
   */
  export const getUserDisplayName = (user) => {
    if (!user) return 'Гость';
    
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    
    if (user.name) {
      return user.name;
    }
    
    if (user.email) {
      return user.email;
    }
    
    return 'Пользователь';
  };
  
  /**
   * Получить инициалы пользователя для аватара
   * @param {Object} user - Данные пользователя
   * @returns {string} Инициалы (максимум 2 символа)
   */
  export const getUserInitials = (user) => {
    if (!user) return 'G';
    
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    
    if (user.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return user.name[0].toUpperCase();
    }
    
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    
    return 'U';
  };

  /**
   * Проверить, является ли маршрут публичным
   * @param {string} pathname - Путь для проверки
   * @returns {boolean} true если маршрут публичный
   */
  export const isPublicRoute = (pathname) => {
    const PUBLIC_ROUTES = [
      '/',
      '/about',
      '/pricing', 
      '/marketplace',
      '/login',
      '/register',
      '/forgot-password',
      '/resend-verification',
      '/docs',
      '/404',
      '/channel-discovery',
      '/video-discovery'
    ];

    const PUBLIC_ROUTE_PATTERNS = [
      '/verify-email/',
      '/reset-password/',
      '/password-reset/',
      '/video-info/'
    ];

    // Точное совпадение
    if (PUBLIC_ROUTES.includes(pathname)) {
      return true;
    }
    
    // Совпадение по паттерну
    return PUBLIC_ROUTE_PATTERNS.some(pattern => 
      pathname.startsWith(pattern)
    );
  };
  
  export default {
    getUser,
    setUser,
    clearUser,
    getUserLanguage,
    setUserLanguage,
    clearAuthData,
    formatAuthError,
    getAuthHeaders,
    hasRole,
    isAdmin,
    getUserDisplayName,
    getUserInitials,
    isPublicRoute,
  };